import { useState } from 'react';
import Modal from '@/components/base/Modal';
import Button from '@/components/base/Button';
import type { Merchant } from '@/types/portal';

interface GoodsFavouritesModalProps {
  open: boolean;
  onClose: () => void;
  merchants: Merchant[];
  onToggle: (id: string) => void;
  onAdd: (name: string, till: string) => void;
}

/**
 * Save a new merchant and pin or unpin the merchants shown in the favourites
 * row on the Buy Goods screen. Saved merchants persist between sessions.
 */
export default function GoodsFavouritesModal({
  open,
  onClose,
  merchants,
  onToggle,
  onAdd,
}: GoodsFavouritesModalProps) {
  const [name, setName] = useState('');
  const [till, setTill] = useState('');
  const [error, setError] = useState('');

  const handleAdd = () => {
    const trimmedName = name.trim();
    const digits = till.replace(/\D/g, '');
    if (!trimmedName) {
      setError('Give this merchant a name.');
      return;
    }
    if (!/^\d{5,7}$/.test(digits)) {
      setError('Enter a valid till number (5–7 digits).');
      return;
    }
    if (merchants.some((merchant) => merchant.till === digits)) {
      setError('That till number is already saved.');
      return;
    }
    onAdd(trimmedName, digits);
    setName('');
    setTill('');
    setError('');
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      icon="ri-star-line"
      title="Favourite merchants"
      subtitle="Add a merchant, then pin the ones you pay most often for one-tap access."
      footer={
        <Button icon="ri-check-line" onClick={onClose}>
          Done
        </Button>
      }
    >
      {/* Add a merchant */}
      <section className="rounded-lg border border-background-200 bg-background-100 p-4">
        <h3 className="font-heading text-sm font-semibold text-foreground-950">Add a merchant</h3>
        <p className="mt-0.5 text-xs text-foreground-500">
          Save a merchant and till number so it is ready next time.
        </p>
        <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
          <input
            value={name}
            onChange={(event) => {
              setName(event.target.value);
              if (error) setError('');
            }}
            placeholder="Merchant name"
            aria-label="Merchant name"
            className="h-10 w-full rounded-lg border border-background-300 bg-background-50 px-3 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none"
          />
          <input
            value={till}
            inputMode="numeric"
            onChange={(event) => {
              setTill(event.target.value.replace(/\D/g, ''));
              if (error) setError('');
            }}
            placeholder="Till number"
            aria-label="Till number"
            className="h-10 w-full rounded-lg border border-background-300 bg-background-50 px-3 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none"
          />
        </div>
        {error ? (
          <p className="mt-2 flex items-center gap-1.5 text-xs text-primary-700">
            <i className="ri-error-warning-line" />
            {error}
          </p>
        ) : null}
        <Button icon="ri-add-line" size="sm" onClick={handleAdd} className="mt-3">
          Add merchant
        </Button>
      </section>

      {/* Saved merchants */}
      <div className="mt-5">
        <h3 className="font-heading text-sm font-semibold text-foreground-950">Saved merchants</h3>
        {merchants.length === 0 ? (
          <p className="mt-3 text-sm text-foreground-500">No saved merchants yet.</p>
        ) : (
          <ul className="mt-1 divide-y divide-background-100">
            {merchants.map((merchant) => {
              const isFavourite = merchant.favourite;
              return (
                <li key={merchant.id} className="flex items-center gap-3 py-3">
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-accent-100 text-sm font-semibold text-accent-800">
                    {merchant.mark}
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium text-foreground-950">{merchant.name}</p>
                    <p className="truncate text-xs text-foreground-500">
                      {merchant.category} · Till {merchant.till}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => onToggle(merchant.id)}
                    aria-pressed={isFavourite}
                    aria-label={
                      isFavourite
                        ? `Remove ${merchant.name} from favourites`
                        : `Add ${merchant.name} to favourites`
                    }
                    className={`flex h-8 w-8 shrink-0 cursor-pointer items-center justify-center rounded-full text-base transition-colors ${
                      isFavourite
                        ? 'text-accent-500 hover:bg-accent-50'
                        : 'text-foreground-300 hover:bg-background-200 hover:text-foreground-500'
                    }`}
                  >
                    <i className={isFavourite ? 'ri-star-fill' : 'ri-star-line'} />
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </Modal>
  );
}